import { FC } from 'react'
import {
  Control,
  Controller,
  UseFormRegister,
  useFormState,
} from 'react-hook-form'
import FormElement from 'components/form/FormElement'
import { IconPerson, IconMail } from 'icons'
import Input from 'components/form/Input'
import Typography from 'components/Typography'
import PhoneInput from 'components/form/PhoneInput'
import { TBuyerFormValues } from '../types'
import styles from './Form.module.scss'
import { validatePhoneNumber } from 'lib/phone'

export type TProps = {
  register: UseFormRegister<TBuyerFormValues>
  control: Control<TBuyerFormValues>
}

const emptyField = {
  message: 'O preenchimento desse campo é obrigatório',
  value: true,
}

const Form: FC<TProps> = ({ register, control }) => {
  const { errors } = useFormState({ control })

  const getInputStatus = (error?: string) => (error ? 'error' : undefined)

  return (
    <form data-testid="ecVisitModalForm">
      <div className={styles.ecVisitModalFormDescription}>
        <Typography variant="TitleSmall" bold>
          Confirme seus dados para solicitar a visita
        </Typography>
        <Typography variant="ParagraphSmall">
          Só entramos em contato para confirmar a visita. Não compartilhamos
          seus dados com terceiros.
        </Typography>
      </div>
      <div className={styles.ecVisitModalForm}>
        <FormElement
          status={getInputStatus(errors.name?.message)}
          title={{
            variant: 'label',
            value: 'Nome',
            htmlFor: 'name',
            required: true,
          }}
          helpText={{
            ariaDescribeId: 'name',
            value: errors.name?.message,
          }}
        >
          <Input
            leadingContent={{
              variant: 'icon',
              Icon: {
                Component: IconPerson,
                title: 'Ícone: pessoa',
                color: 'grey900',
              },
            }}
            inputElementProps={{
              id: 'name',
              placeholder: 'Ex: Maria da Silva',
              ...register('name', {
                required: emptyField,
              }),
              required: true,
            }}
            status={getInputStatus(errors.name?.message)}
          />
        </FormElement>

        <FormElement
          status={getInputStatus(errors.phone?.message)}
          title={{
            variant: 'label',
            value: 'Telefone com Whatsapp',
            htmlFor: 'phone',
            required: true,
          }}
          helpText={{
            ariaDescribeId: 'phone',
            value: errors.phone?.message,
          }}
        >
          <Controller
            control={control}
            name="phone"
            rules={{
              required: emptyField,
              minLength: {
                message: 'Digite um número válido',
                value: 14,
              },
              validate: {
                validPhone: (phone) => {
                  const isValidNumber = validatePhoneNumber(phone)
                  return (
                    isValidNumber ||
                    'Telefone inválido. Verifique o número informado.'
                  )
                },
              },
            }}
            render={({ field: { onChange, onBlur, value } }) => (
              <PhoneInput
                name="phone"
                type="tel"
                id="phone"
                value={value}
                handleOnChange={onChange}
                handleOnBlur={onBlur}
                placeholder="Ex: (11) 99999-9999"
                status={getInputStatus(errors.phone?.message)}
              />
            )}
          />
        </FormElement>
        <FormElement
          title={{
            variant: 'label',
            value: 'E-mail',
            htmlFor: 'email',
          }}
        >
          <Input
            leadingContent={{
              variant: 'icon',
              Icon: {
                Component: IconMail,
                title: 'Ícone: e-mail',
                color: 'grey900',
              },
            }}
            inputElementProps={{
              id: 'email',
              type: 'email',
              placeholder: 'nome@email.com',
              ...register('email', {
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i,
                  message: 'Preencha um e-mail válido',
                },
              }),
            }}
          />
        </FormElement>
      </div>
    </form>
  )
}

export default Form
